import { useEffect, useState } from "react";
import { fmtDate, fmtNum, type Period } from "@am/shared";
import { toast } from "sonner";
import { useApiClient } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import { useTheme } from "../theme/ThemeProvider";
import { PageHeader } from "../components/PageHeader";
import { TimelineChart } from "../charts/TimelineChart";
import { TokensChart } from "../charts/TokensChart";
import { ConfirmModal } from "../components/ConfirmModal";

interface OverviewResponse {
  total_requests: number;
  unique_sessions: number;
  cache_hits: number;
  avg_latency_ms: number;
  estimated_cost_usd: number;
  error_count: number;
}

interface TimelinePoint {
  hour?: string;
  period?: string;
  count?: number;
}

interface TokensPoint {
  hour?: string;
  period?: string;
  input?: number;
  output?: number;
}

interface TopQueryRow {
  query: string;
  count: number;
}

interface ErrorRow {
  error?: string;
  timestamp?: string;
}

const PERIODS: Period[] = ["24h", "7d", "30d"];

export function StatsPage() {
  const api = useApiClient();
  const { t } = useI18n();
  const { theme } = useTheme();


  const [period, setPeriod] = useState<Period>("24h");
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [tokens, setTokens] = useState<TokensPoint[]>([]);
  const [topQueries, setTopQueries] = useState<TopQueryRow[]>([]);
  const [errors, setErrors] = useState<ErrorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearingErrors, setClearingErrors] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [ov, tl, tk, tq, er] = await Promise.all([
          api<OverviewResponse>(`/tenant/api/stats/overview?period=${period}`),
          api<TimelinePoint[]>(`/tenant/api/stats/timeline?period=${period}`),
          api<TokensPoint[]>(`/tenant/api/stats/tokens?period=${period}`),
          api<TopQueryRow[]>(`/tenant/api/stats/top-queries?period=${period}&limit=10`),
          api<ErrorRow[]>(`/tenant/api/stats/errors?limit=20`),
        ]);
        if (cancelled) return;
        setOverview(ov);
        setTimeline(tl ?? []);
        setTokens(tk ?? []);
        setTopQueries(tq ?? []);
        setErrors(er ?? []);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof Error ? err.message : t("error_generic"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, period, t, toast]);

  async function flushCache() {
    try {
      const d = await api<{ deleted_keys?: number }>("/tenant/api/cache/flush", { method: "POST" });
      toast.success(`${t("flush_ok")} (${d?.deleted_keys ?? 0} clés)`);
      // Trigger a reload by re-setting the period (no-op state change still
      // re-runs the effect because period is in its deps array).
      setPeriod((p) => p);
    } catch {
      toast.error(t("flush_err"));
    }
  }

  async function doClearErrors() {
    setClearingErrors(true);
    try {
      const d = await api<{ cleared?: number }>("/tenant/api/stats/errors", { method: "DELETE" });
      toast.success(`${t("clear_errors_ok")} (${d?.cleared ?? 0})`);
      setErrors([]);
    } catch {
      toast.error(t("clear_errors_err"));
    } finally {
      setClearingErrors(false);
    }
  }

  const cacheRate =
    overview && overview.total_requests > 0
      ? Math.round((overview.cache_hits / overview.total_requests) * 100)
      : null;

  return (
    <div className="page is-active">
      <PageHeader titleKey="stats_title" subtitleKey="stats_subtitle">
        <div className="period-switch">
          {PERIODS.map((p) => (
            <button
              key={p}
              className={"period-btn" + (period === p ? " is-active" : "")}
              type="button"
              onClick={() => setPeriod(p)}
            >
              {t(`period_${p}` as `period_${Period}`)}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="stat-grid">
        <StatCard label={t("stat_requests")} value={loading ? "…" : fmtNum(overview?.total_requests ?? 0)} />
        <StatCard label={t("stat_sessions")} value={loading ? "…" : fmtNum(overview?.unique_sessions ?? 0)} />
        <StatCard
          label={t("stat_cache_rate")}
          value={loading ? "…" : cacheRate === null ? "0%" : `${cacheRate}%`}
          valueClass="orange"
        />
        <StatCard
          label={t("stat_avg_latency")}
          value={loading ? "…" : overview?.avg_latency_ms ? `${Math.round(overview.avg_latency_ms)} ms` : "—"}
        />
        <StatCard
          label={t("stat_cost")}
          value={loading ? "…" : `$${(overview?.estimated_cost_usd ?? 0).toFixed(4)}`}
          valueClass="green"
        />
        <StatCard label={t("stat_errors")} value={loading ? "…" : fmtNum(overview?.error_count ?? 0)} />
      </div>

      <div className="charts-grid">
        <div className="card">
          <div className="card-header">
            <span className="card-title">{t("chart_requests")}</span>
          </div>
          <div className="chart-wrap">
            <TimelineChart data={timeline} label={t("stat_requests")} theme={theme} />
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <span className="card-title">{t("chart_tokens")}</span>
          </div>
          <div className="chart-wrap">
            <TokensChart data={tokens} theme={theme} />
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="card">
          <div className="card-header">
            <span className="card-title">{t("top_queries")}</span>
            <button className="btn-sm btn-ghost" type="button" onClick={flushCache}>
              {t("flush_cache")}
            </button>
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            <table>
              <tbody>
                {loading ? (
                  <tr><td className="empty-state"><span className="spin" /></td></tr>
                ) : topQueries.length === 0 ? (
                  <tr><td className="empty-state">{t("stat_requests")} — 0</td></tr>
                ) : (
                  topQueries.map((row, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 12, color: "var(--text-secondary)", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.query}
                      </td>
                      <td className="mono" style={{ textAlign: "right", paddingRight: 16 }}>
                        {row.count}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">{t("recent_errors")}</span>
            <button
              className="btn-sm btn-ghost"
              type="button"
              onClick={() => setConfirmClear(true)}
              disabled={clearingErrors}
            >
              {t("clear_errors")}
            </button>
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            <table>
              <tbody>
                {loading ? (
                  <tr><td className="empty-state"><span className="spin" /></td></tr>
                ) : errors.length === 0 ? (
                  <tr><td className="empty-state">—</td></tr>
                ) : (
                  errors.slice(0, 10).map((row, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 12, color: "var(--red)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.error ?? ""}
                      </td>
                      <td className="mono" style={{ whiteSpace: "nowrap", fontSize: 11 }}>
                        {fmtDate(row.timestamp)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmClear}
        title={t("clear_errors_title")}
        body={t("clear_errors_confirm")}
        confirmLabel={t("clear_errors")}
        cancelLabel={t("cancel")}
        danger
        onConfirm={async () => {
          setConfirmClear(false);
          await doClearErrors();
        }}
        onCancel={() => setConfirmClear(false)}
      />
    </div>
  );
}

function StatCard({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className={"stat-value" + (valueClass ? " " + valueClass : "")}>{value}</div>
    </div>
  );
}
